import type { Capability, LoginResult, RoleRef, Section, SessionUser } from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api, describeApiError } from './api';

interface SessionValue {
  user: SessionUser | null;
  /** Union of every role held: what the API will allow. */
  capabilities: Capability[];
  /** Every role the user holds — usually one. */
  roles: RoleRef[];
  /** The role whose workspace is being shown. Presentation only. */
  activeRole: RoleRef | null;
  /** Screens the active workspace presents. */
  sections: Section[];
  /** Switch workspace. Never changes permissions — every role still counts. */
  setActiveRole: (roleId: string) => void;
  /** Is this screen part of the workspace currently being shown? */
  shows: (section: Section) => boolean;
  /** True only while we genuinely do not know yet (first load). */
  isLoading: boolean;
  isAuthenticated: boolean;
  can: (capability: Capability) => boolean;
  /** True when the account must replace its password before doing anything else. */
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  /** Edit your own display name; refreshes the cached session. */
  updateProfile: (name: string) => Promise<void>;
  isUpdatingProfile: boolean;
  /** Change your own password. Signs you out everywhere on success. */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  isChangingPassword: boolean;
  /**
   * A one-off message for the sign-in screen — currently "your password changed".
   * Held in state rather than router location because clearing the session makes
   * the protected layout redirect to /login on its own, and a second navigation
   * would race it and drop the message.
   */
  authNotice: string | null;
  dismissAuthNotice: () => void;
  loginError: string | null;
  isLoggingIn: boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  /**
   * A 401 here is a normal state ("signed out"), not an error worth retrying,
   * so retries are off and the query is allowed to settle as unauthenticated.
   */
  const sessionQuery = useQuery({
    queryKey: ['session'],
    queryFn: api.auth.me,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  /**
   * Which workspace is being shown, when the user holds more than one role.
   *
   * Held in memory rather than persisted on purpose: the server picks the most
   * capable role as the starting view, so a shared terminal always opens on the
   * same thing for the same person rather than on whatever the last visitor
   * chose. It is not a permission, so it needs no round trip and no storage.
   */
  const [pickedRoleId, setPickedRoleId] = useState<string | null>(null);

  /** A one-off message for the sign-in screen; cleared once it has served. */
  const [authNotice, setAuthNotice] = useState<string | null>(null);

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      api.auth.login(email, password),
    onSuccess: (result) => {
      // A different person is signing in, so the previous view does not carry
      // over — the server's default applies again.
      setPickedRoleId(null);
      setAuthNotice(null);
      queryClient.setQueryData(['session'], {
        user: result.user,
        capabilities: result.capabilities,
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      setPickedRoleId(null);
      queryClient.setQueryData(['session'], null);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: (name: string) => api.auth.updateProfile({ name }),
    onSuccess: (result) => {
      queryClient.setQueryData(['session'], {
        user: result.user,
        capabilities: result.capabilities,
      });
    },
  });

  /**
   * Changing a password destroys every session server-side, including this one.
   * Treated exactly like signing out: clear the cache and let the protected
   * layout send the person back to the sign-in screen.
   */
  const changePasswordMutation = useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => api.auth.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      setPickedRoleId(null);
      queryClient.setQueryData(['session'], null);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
      // The protected layout will bounce to /login; this is what that screen
      // shows, since the redirect would otherwise overwrite a router-state note.
      setAuthNotice('Password changed. Sign in again with your new password.');
    },
  });

  const user = sessionQuery.data?.user ?? null;
  const capabilities = useMemo(() => sessionQuery.data?.capabilities ?? [], [sessionQuery.data]);
  const roles = useMemo(() => user?.roles ?? [], [user]);

  const activeRole = useMemo(() => {
    const wanted = pickedRoleId ?? user?.defaultRoleId ?? null;
    // Falling back to the first role covers a pick that is no longer held —
    // somebody edited the user's roles while their tab was open.
    return roles.find((role) => role.id === wanted) ?? roles[0] ?? null;
  }, [roles, pickedRoleId, user?.defaultRoleId]);

  const sections = useMemo(() => activeRole?.sections ?? [], [activeRole]);

  const can = useCallback(
    (capability: Capability) => capabilities.includes(capability),
    [capabilities],
  );

  /**
   * With no roles at all there is no workspace to scope, so nothing is hidden.
   * That fails *open* rather than closed, which is safe here precisely because
   * this only decides what the sidebar lists — the API decides what anybody may
   * actually do, and a user with no roles can do nothing.
   */
  const shows = useCallback(
    (section: Section) => activeRole === null || sections.includes(section),
    [activeRole, sections],
  );

  const setActiveRole = useCallback((roleId: string) => setPickedRoleId(roleId), []);

  const login = useCallback(
    async (email: string, password: string) => {
      return loginMutation.mutateAsync({ email, password });
    },
    [loginMutation],
  );

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const updateProfile = useCallback(
    async (name: string) => {
      await updateProfileMutation.mutateAsync(name);
    },
    [updateProfileMutation],
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      await changePasswordMutation.mutateAsync({ currentPassword, newPassword });
    },
    [changePasswordMutation],
  );

  const dismissAuthNotice = useCallback(() => setAuthNotice(null), []);

  const loginError = useMemo(() => {
    const error = loginMutation.error;
    if (!error) return null;
    if (error instanceof ApiError) {
      // The API's own `message` for a rejected body is the uselessly generic
      // "Validation failed"; describeApiError surfaces the per-field text (e.g.
      // "password: String must contain at least 8 character(s)") and otherwise
      // falls back to the message (bad credentials, throttled, …).
      return describeApiError(error);
    }
    return 'Could not reach the server. Is the API running?';
  }, [loginMutation.error]);

  const value: SessionValue = {
    user,
    capabilities,
    roles,
    activeRole,
    sections,
    setActiveRole,
    shows,
    isLoading: sessionQuery.isPending,
    isAuthenticated: user !== null,
    can,
    mustChangePassword: user?.mustChangePassword ?? false,
    login,
    logout,
    updateProfile,
    isUpdatingProfile: updateProfileMutation.isPending,
    changePassword,
    isChangingPassword: changePasswordMutation.isPending,
    authNotice,
    dismissAuthNotice,
    loginError,
    isLoggingIn: loginMutation.isPending,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useSession must be used inside a <SessionProvider>');
  }
  return value;
}

/**
 * Gate a control on a capability. This only hides UI — the API enforces the
 * same rule. A hidden button is not a permission.
 */
export function Can({
  capability,
  children,
  fallback = null,
}: {
  capability: Capability;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = useSession();
  return <>{can(capability) ? children : fallback}</>;
}
